<template>
  <form @submit="onSubmit">
    <BaseCard>
      <div class="space-y-2">
        <FormInputText name="name" label="Name" />
        <FormInputChecklist
          name="permissions"
          label="Permissions"
          :options="selectOptions"
          disable-null
        />
      </div>
    </BaseCard>
    <div class="mt-4 flex justify-end space-x-2">
      <FormSubmitButton :is-loading="isSubmitting">
        {{ roleId ? "Update" : "Create" }} Role
      </FormSubmitButton>
    </div>
  </form>

  <BaseDialog :is-open="isErrorModalOpen">
    <div class="flex flex-col items-center gap-4 p-3">
      <h2 class="text-center text-lg font-semibold"
        >Something went wrong</h2
      >
      <p class="text-center text-sm text-error"
        >Something went wrong fetching role</p
      >
      <RouterLink :to="{ name: 'roleIndex' }">
        <ButtonPrimary>Return to role list</ButtonPrimary>
      </RouterLink>
    </div>
  </BaseDialog>
</template>

<script setup lang="ts">
import { useCreateEditRoleForm } from "@/composables/forms/useCreateEditRoleForm"

const props = defineProps<{
  roleId?: string
}>()

const emit = defineEmits<{
  (event: "submit"): void
}>()

const { data: permissions } = usePermissions()

const selectOptions = computed(
  () => permissions.value || []
)

const { handleSubmit, setErrors, isSubmitting, resetForm } =
  useCreateEditRoleForm()

const isLoading = ref(false)
const isErrorModalOpen = ref(false)
onMounted(async () => {
  if (props.roleId) {
    try {
      isLoading.value = true
      const { id, ...role } = await retrieveRole(
        props.roleId
      )
      resetForm({ values: role })
    } catch {
      isErrorModalOpen.value = true
    } finally {
      isLoading.value = false
    }
  }
})

const onSubmit = handleSubmit(async (formValues) => {
  try {
    if (props.roleId) {
      await updateRole(props.roleId, formValues)
      toast.success("Role updated")
    } else {
      await createRole(formValues)
      toast.success("Role created")
    }
    emit("submit")
  } catch (error) {
    if (error instanceof ApiError) {
      setErrors(error.requestErrors?.body ?? {})
      toast.error(
        error.requestErrors?.body?.name ??
          (props.roleId
            ? "Failed to update role"
            : "Failed to create role")
      )
    } else if (error instanceof Error) {
      setErrors(JSON.parse(error.message))
    }
  }
})
</script>
